import React, { useState } from "react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Check, Zap, Users, LogOut, Lock, ShieldCheck, KeyRound, Scale, X as XMark } from "lucide-react";
import { toast } from "sonner";

const PRO_FEATURES = [
  "Pro Plan · Entry-Level Tax Tracking ($9.99/mo)",
  "Unlimited Core Receipt OCR Uploads (GPT Vision)",
  "Digital Expense Ledger Archive",
  "2-Way Text Messaging Ingestion",
  "Real-Time 'useCountUp' Mileage Deduction Ticker",
  "Auto-categorization & tags",
  "Fuzzy full-text search + CSV/PDF export",
  "IRS-compliant archiving",
  "AI Audit-Shield & Discrepancy Alerts",
];
const BUSINESS_FEATURES = [
  "Business Plan · Team Expansion & Optimization ($19.99/mo)",
  "Everything in Pro, plus:",
  "Multi-Seat Crew Manifests (invite unlimited team members)",
  "Internal Support Ticket Vault (Help-Desk drawer + admin console)",
  "Algorithmic B2B Arbitrage Radar",
  "Live Neon Reactor Price Intelligence Ticker",
  "Team Expense Approval Workflows",
  "Virtual Card Control Hub",
  "Corporate Compliance Enforcer",
  "Bank feed reconciliation + Priority OCR queue",
];

const PREMIUM_SHIELD_FEATURES = [
  "Premium Tax Shield · Sovereign Wealth Fortress ($29.99/mo · ELITE)",
  "Everything in Business, plus:",
  "⚡ Cash Flash Engine Access (Audited POS Tax Reserves Matrix)",
  "🌌 Nebula B2B Invoices & Client Payment Clearing (Send custom branded payment rows · 1% platform skim)",
  "🛡️ Premium Tax Shield Multi-Vault Suite (Advanced Auto, Home, & Capital Asset tracking)",
  "📁 Tax Preparation Free Portfolio (Instant 5-Page Pre-Hydrated IRS Form 1040 + Schedule C Generation Packets)",
  "Real-Time Paycheck Stub Forecasting Odometer",
  "12-Week Trajectory SVG Charting",
  "Native Mobile Hardware Camera Capture (tap-to-focus + HEIC re-encoding)",
  "HSA 'Triple Shield' Odometer + 30-Year Wealth Projection",
  "Backdoor Roth IRA Conversion Matrix (§ 408A(c)(3), Notice 2014-54)",
  "Solo 401(k) Retirement Runway (§ 402(g), § 415(c))",
  "1-Click Unified CPA Handoff PDF Bundle Exporter",
  "White-Glove Audit Response + Quarterly Tax Vault",
  "Dedicated Oracle CFO fine-tune with your ledger history",
];

function PlanCard({ tier, price, priceSub, features, popular, elite, onClick, loading, testid }) {
  const eliteFrame = elite && !popular;
  return (
    <div
      className={`relative rounded-3xl p-8 sm:p-10 flex flex-col h-full ${
        popular
          ? "glass emerald-glow pulse-emerald"
          : eliteFrame
            ? "glass premium-shield-glow"
            : "glass"
      }`}
      data-testid={`plan-${tier.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {popular && (
        <div
          className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-[0.18em] bg-emerald-500 text-slate-950"
          data-testid="most-popular-badge"
        >
          Most Popular
        </div>
      )}
      {eliteFrame && (
        <div
          className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-[0.18em] text-slate-950"
          style={{ background: "linear-gradient(135deg, #a855f7, #06B6D4)" }}
          data-testid="elite-badge"
        >
          Premium Shield · Elite
        </div>
      )}
      {popular && (
        <div
          data-testid="ai-suite-badge"
          className="absolute -top-3 right-6 px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-[0.2em] bg-slate-950/80 border border-emerald-400/70 text-emerald-300 pulse-emerald"
          style={{
            boxShadow:
              "0 0 12px rgba(16,185,129,0.55), 0 0 28px rgba(16,185,129,0.35), inset 0 0 8px rgba(16,185,129,0.15)",
          }}
        >
          ✦ Advanced Financial AI Suite
        </div>
      )}
      <div className="flex items-center gap-3 mb-2">
        {popular ? (
          <Users size={22} className="text-emerald-400" />
        ) : (
          <Zap size={22} className="text-emerald-400" />
        )}
        <h3 className="text-2xl font-bold tracking-tight text-white">{tier}</h3>
      </div>
      <div className="text-slate-400 text-sm">
        {tier === "Pro"
          ? "For individual power users & consumers"
          : "For freelancers & small teams"}
      </div>

      <div className="mt-8 mb-2 flex items-baseline gap-2">
        {tier === "Pro" && (
          <span
            className="text-slate-500 font-mono text-lg line-through opacity-70"
            data-testid="pro-anchor-price"
            aria-label="Regular price $11.99, discounted"
          >
            $11.99
          </span>
        )}
        <span className="text-5xl font-mono font-bold text-white tracking-tight" data-testid={`${tier.toLowerCase()}-price`}>
          ${price}
        </span>
        <span className="text-slate-400 font-mono text-sm">/month</span>
      </div>
      {priceSub && (
        <div className="mb-6 text-xs font-mono text-emerald-300/80" data-testid={`${tier.toLowerCase()}-price-sub`}>
          {priceSub}
        </div>
      )}
      {!priceSub && <div className="mb-6" />}

      <ul className="space-y-3 mb-10 flex-1">
        {features.map((f, i) => {
          const isLead = i === 0;
          const isContrast =
            (tier === "Pro" && i === 1) || (tier === "Business" && i === 2);
          return (
            <li
              key={i}
              className="flex items-start gap-3 text-sm"
              data-testid={`plan-${tier.toLowerCase()}-feature-${i}`}
            >
              <div
                className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                  isLead || isContrast ? "bg-emerald-500/25" : "bg-emerald-500/10"
                }`}
              >
                <Check size={12} className="text-emerald-400" strokeWidth={3} />
              </div>
              <span
                className={
                  isLead
                    ? "font-semibold text-emerald-300"
                    : isContrast
                    ? "text-emerald-200/95"
                    : "text-slate-200"
                }
              >
                {f}
              </span>
            </li>
          );
        })}
      </ul>

      <button
        onClick={onClick}
        disabled={loading}
        data-testid={testid}
        className={`btn-neon h-14 w-full rounded-xl font-semibold text-base ${
          popular
            ? "bg-emerald-500 text-slate-950 hover:bg-emerald-400"
            : "bg-white/10 text-white hover:bg-white/15 border border-white/15"
        } ${loading ? "opacity-60 cursor-wait" : ""}`}
      >
        {loading ? "Redirecting to Stripe…" : `Start ${tier}`}
      </button>
      <div className="mt-4 text-center text-xs text-slate-500 font-mono uppercase tracking-widest">
        Instant activation · Cancel anytime
      </div>
    </div>
  );
}

export default function Paywall() {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(null);
  const [billingCycle, setBillingCycle] = useState("monthly"); // "monthly" | "annual"
  // 20% compound annual discount — prices already minted server-side per lookup_key.
  const PRICES = {
    pro:            { monthly: "9.99",  annual: "95.99",  monthlyEq: "7.99" },
    business:       { monthly: "19.99", annual: "191.99", monthlyEq: "15.99" },
    premium_shield: { monthly: "29.99", annual: "287.99", monthlyEq: "23.99" },
  };
  const lookupFor = (base) => `${base}_${billingCycle}`;
  const priceFor = (tier) => billingCycle === "annual" ? PRICES[tier].monthlyEq : PRICES[tier].monthly;
  const priceSubFor = (tier) => billingCycle === "annual"
    ? `$${PRICES[tier].annual}/yr · save 20%`
    : (tier === "pro" ? "flat monthly · no seats, no add-ons"
       : tier === "business" ? "base + $7.99 per user seat / month"
       : "every Business feature + real-time tax shielding");

  const startCheckout = async (lookup_key) => {
    setLoading(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen grid-bg">
      <header className="px-8 py-6 flex items-center justify-between">
        <Logo size="md" />
        <div className="flex items-center gap-4">
          <div className="hidden sm:flex items-center gap-2 text-slate-400 text-sm">
            <Lock size={14} />
            Signed in as{" "}
            <span className="text-slate-200 font-mono">{user?.email}</span>
          </div>
          <button
            onClick={logout}
            data-testid="paywall-logout-btn"
            className="text-slate-400 hover:text-white text-sm flex items-center gap-1.5"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-6 pb-24 pt-4">
        <div className="text-center max-w-2xl mx-auto mb-14 fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 text-xs font-semibold uppercase tracking-widest mb-6">
            <Lock size={12} /> Premium-only Access
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-[1.05]">
            Pick a plan to <span className="text-emerald-400">unlock the vault</span>.
          </h1>
          <p className="text-slate-300 mt-5 text-lg">
            Receipt Sheets is a premium-only app. Choose Pro for personal expense
            control, or go Business for line-item receipt intelligence and team
            reconciliation.
          </p>
        </div>

        <div className="flex justify-center mb-6" data-testid="billing-cycle-toggle">
          <div
            className="inline-flex items-center gap-0.5 p-1 rounded-full"
            style={{
              background: "rgba(15,23,42,0.7)",
              border: "1px solid rgba(16,185,129,0.35)",
            }}
          >
            {[
              { key: "monthly", label: "Monthly" },
              { key: "annual", label: "Annual · Save 20%" },
            ].map((opt) => {
              const active = billingCycle === opt.key;
              return (
                <button
                  key={opt.key}
                  onClick={() => setBillingCycle(opt.key)}
                  data-testid={`billing-cycle-${opt.key}`}
                  data-active={active ? "true" : "false"}
                  className={`px-4 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-[0.18em] transition-colors ${
                    active ? "text-slate-950" : "text-emerald-200 hover:text-white"
                  }`}
                  style={{
                    background: active
                      ? "linear-gradient(135deg, #10B981, #06B6D4)"
                      : "transparent",
                    boxShadow: active ? "0 6px 14px -6px rgba(16,185,129,0.6)" : "none",
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-6 items-stretch max-w-6xl mx-auto stagger">
          <PlanCard
            tier="Pro"
            price={priceFor("pro")}
            priceSub={priceSubFor("pro")}
            features={PRO_FEATURES}
            onClick={() => startCheckout(lookupFor("pro"))}
            loading={loading === lookupFor("pro")}
            testid="checkout-pro-btn"
          />
          <PlanCard
            tier="Business"
            price={priceFor("business")}
            priceSub={priceSubFor("business")}
            features={BUSINESS_FEATURES}
            popular
            onClick={() => startCheckout(lookupFor("business"))}
            loading={loading === lookupFor("business")}
            testid="checkout-business-btn"
          />
          <PlanCard
            tier="Premium Tax Shield"
            price={priceFor("premium_shield")}
            priceSub={priceSubFor("premium_shield")}
            features={PREMIUM_SHIELD_FEATURES}
            elite
            onClick={() => startCheckout(lookupFor("premium_shield"))}
            loading={loading === lookupFor("premium_shield")}
            testid="checkout-premium-shield-btn"
          />
        </div>

        {/* Trust badge strip — enterprise-positioning pills */}
        <div
          data-testid="trust-badge-strip"
          className="mt-10 max-w-3xl mx-auto flex flex-wrap items-center justify-center gap-3"
        >
          {[
            { key: "soc2", icon: ShieldCheck, label: "SOC 2 Type II", sub: "Attested" },
            { key: "aes", icon: KeyRound, label: "256-bit AES", sub: "At Rest + In Transit" },
            { key: "gdpr", icon: Scale, label: "GDPR", sub: "EU + UK Ready" },
          ].map(({ key, icon: Icon, label, sub }) => (
            <div
              key={key}
              data-testid={`trust-badge-${key}`}
              className="trust-badge glass rounded-full pl-3 pr-4 py-2 flex items-center gap-2.5 border"
              style={{
                borderColor: "rgba(16,185,129,0.28)",
                background: "linear-gradient(120deg, rgba(16,185,129,0.08), rgba(15,23,42,0.85))",
              }}
            >
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                style={{
                  background: "rgba(16,185,129,0.15)",
                  boxShadow: "inset 0 0 0 1px rgba(16,185,129,0.35), 0 0 12px rgba(16,185,129,0.28)",
                }}
              >
                <Icon size={14} className="text-emerald-300" />
              </div>
              <div className="leading-tight">
                <div className="text-white text-[13px] font-semibold tracking-tight">{label}</div>
                <div className="text-[9px] font-mono uppercase tracking-widest text-emerald-300/70">
                  {sub}
                </div>
              </div>
            </div>
          ))}
          <style>{`
            .trust-badge {
              transition: transform 220ms cubic-bezier(0.22,1,0.36,1),
                          box-shadow 220ms ease-out,
                          border-color 220ms ease-out;
            }
            .trust-badge:hover {
              transform: translateY(-2px);
              border-color: rgba(16,185,129,0.55) !important;
              box-shadow: 0 12px 28px -12px rgba(16,185,129,0.55);
            }
          `}</style>
        </div>

        {/* Comparison column — Receipt Sheets vs Consumer Coupon Apps */}
        <div
          data-testid="comparison-column"
          className="mt-16 max-w-4xl mx-auto"
        >
          <div className="text-center mb-6">
            <div className="text-[10px] font-mono uppercase tracking-[0.32em] text-emerald-400 mb-2">
              Head-to-Head
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-white tracking-tight">
              Receipt Sheets <span className="text-slate-500 font-light">vs.</span>{" "}
              <span className="text-slate-300">Consumer Coupon Apps</span>
            </h3>
            <p className="text-slate-400 text-sm mt-2 max-w-2xl mx-auto">
              We&apos;re an enterprise Schedule C tax engine — not a rewards-tracker that
              monetizes your shopping habits. Compare where every dollar of value comes from.
            </p>
          </div>

          <div className="glass rounded-2xl overflow-hidden border" style={{ borderColor: "rgba(16,185,129,0.28)" }}>
            <div className="grid grid-cols-[1.4fr_1fr_1fr] items-stretch">
              {/* Header row */}
              <div className="px-5 py-4 bg-emerald-500/5 border-b border-white/5">
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Dimension</div>
              </div>
              <div className="px-5 py-4 border-b border-white/5" style={{ background: "linear-gradient(180deg, rgba(16,185,129,0.15), rgba(16,185,129,0.05))" }}>
                <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-300">Receipt Sheets</div>
                <div className="text-emerald-100 text-sm font-semibold mt-0.5">Enterprise · Tax</div>
              </div>
              <div className="px-5 py-4 border-b border-white/5 bg-white/[0.02]">
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Consumer Coupon Apps</div>
                <div className="text-slate-400 text-sm font-semibold mt-0.5">Rebates · Rewards</div>
              </div>

              {[
                {
                  dim: "How they make money",
                  us: "Direct subscription — $9.99 Pro / $19.99 Business",
                  usOk: true,
                  them: "Selling your purchase data to brands and ad networks",
                  themOk: false,
                },
                {
                  dim: "Primary output",
                  us: "IRS-ready Schedule C, itemized deductions, CPA hand-offs",
                  usOk: true,
                  them: "Cashback points, coupon codes, brand emails",
                  themOk: false,
                },
                {
                  dim: "Corporate expense workflows",
                  us: "Manager approvals, Smart Visa authorizations, team seats",
                  usOk: true,
                  them: "Personal receipts only — no team, no controls",
                  themOk: false,
                },
                {
                  dim: "AI capability",
                  us: "GPT-5.2 Vision OCR + Write-Off Predictor + Audit-Shield",
                  usOk: true,
                  them: "Merchant matching against a coupon catalog",
                  themOk: false,
                },
                {
                  dim: "Data residency & retention",
                  us: "SOC 2 · 256-AES · GDPR · you own & export everything",
                  usOk: true,
                  them: "Bundled into anonymized ad-tech data lakes",
                  themOk: false,
                },
                {
                  dim: "Cash-back on your card spend",
                  us: "1% pure cash on Integrated Corporate Smart Visa Cards",
                  usOk: true,
                  them: "Points redeemable only on partner brands",
                  themOk: false,
                },
              ].map((row, i, arr) => {
                const last = i === arr.length - 1;
                const borderCls = last ? "" : "border-b border-white/5";
                const rowCls = `compare-row-cell compare-row-${i}`;
                const onEnter = () => document.querySelectorAll(`.compare-row-${i}`).forEach(el => el.classList.add("compare-row-hover"));
                const onLeave = () => document.querySelectorAll(`.compare-row-${i}`).forEach(el => el.classList.remove("compare-row-hover"));
                return (
                  <React.Fragment key={i}>
                    <div
                      data-testid={`compare-row-${i}`}
                      onMouseEnter={onEnter}
                      onMouseLeave={onLeave}
                      className={`${rowCls} px-5 py-4 text-slate-300 text-sm ${borderCls} bg-emerald-500/[0.03]`}
                    >
                      {row.dim}
                    </div>
                    <div
                      onMouseEnter={onEnter}
                      onMouseLeave={onLeave}
                      className={`${rowCls} px-5 py-4 flex items-start gap-2 text-sm ${borderCls}`}
                      style={{ background: "linear-gradient(180deg, rgba(16,185,129,0.08), transparent)" }}
                    >
                      <span className="shrink-0 w-4 h-4 rounded-full bg-emerald-500/25 mt-0.5 flex items-center justify-center">
                        <Check size={10} className="text-emerald-300" strokeWidth={3} />
                      </span>
                      <span className="text-emerald-100">{row.us}</span>
                    </div>
                    <div
                      onMouseEnter={onEnter}
                      onMouseLeave={onLeave}
                      className={`${rowCls} px-5 py-4 flex items-start gap-2 text-sm ${borderCls} bg-white/[0.01]`}
                    >
                      <span className="shrink-0 w-4 h-4 rounded-full bg-rose-500/15 mt-0.5 flex items-center justify-center">
                        <XMark size={10} className="text-rose-300" strokeWidth={3} />
                      </span>
                      <span className="text-slate-400">{row.them}</span>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
          <style>{`
            .compare-row-cell {
              transition: background-color 220ms ease-out, box-shadow 220ms ease-out;
              position: relative;
            }
            .compare-row-hover {
              background-color: rgba(16,185,129,0.08) !important;
              box-shadow: inset 0 0 0 1px rgba(16,185,129,0.25);
            }
          `}</style>
        </div>

        <div className="mt-16 max-w-3xl mx-auto text-center">
          <div className="glass rounded-2xl px-8 py-6 inline-flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
            <span className="font-mono text-emerald-300 text-sm uppercase tracking-widest">
              14-day money-back guarantee
            </span>
            <span className="text-slate-500">·</span>
            <span className="text-slate-300 text-sm">Powered by Stripe</span>
            <span className="text-slate-500">·</span>
            <span className="text-slate-300 text-sm">Cancel with one click</span>
          </div>
        </div>
      </div>
    </div>
  );
}
